import { useFocusEffect, useNavigation } from '@react-navigation/native';
import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import type { CancelToken } from 'axios';
import { Linking, Modal, Platform, Pressable, useWindowDimensions } from 'react-native';
import { ScrollView, View, Image } from 'react-native';
import { SurveyModel, QuestionFileModel, QuestionCommentModel } from 'survey-core';
import { initializeSurveyFileInput } from './surveyFileInput';
import { resolveSurveyFileUrl } from './surveyFileUrl';
import 'survey-core/survey.i18n';
import showdown from 'showdown';
import 'survey-core/survey-core.min.css';
import { ReactElementFactory, Survey } from 'survey-react-ui';
import {
  DeviceTheme,
  GetSurveyFromMissionAssignationViewModel,
  MyMissionClient,
  PartialSaveMissionAnswerRequest,
  SaveMissionAnswerDto,
  SurveyImagesClient,
  SurveyFormModel,
} from '../../../api/api.g';
import useApi from '../../../hooks/api-hook';
import { useAppDispatch, useAppSelector } from '../../../hooks/store-hook';
import { authActions } from '../../../models/helpers/platformImportHelper';
import { missionsActions, MissionCompletionPrompt } from '../../../store/missionsSlice';
import Label, { LabelType } from '../Label';
import useCustomTheme from '../../../hooks/theme-hook';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import { faStore, faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons';
import CustomButton from '../CustomButton';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { StringHelper } from '../../../helpers/stringHelper';
import useTranslation from '../../../hooks/translation-hook';
import useStyles from '../../../hooks/styles-hook';
import Loading from '../Loading';
import { lightBoxActions } from '../../../store/lightBoxSlice';
import ProfileModal from '../../myaccount/myprofile/ProfileModal';
import SurveyCompleted from './SurveyCompleted';
import CompetitorCircuitSurveyCompleted from '../../mymission/CompetitorCircuitSurveyCompleted';
import { getCircuitSurveyContext } from '../../../helpers/competitorCircuitSurvey';
import SurveyError from './SurveyError';
import ImageZoom from 'react-image-zooom';
import { navBarActions } from '../../../store/navBarSlice';
import { userPrefsActions } from '../../../store/userPrefsSlice';
import { LinearGradient } from 'expo-linear-gradient';
import Compressor from 'compressorjs';
import Overlay from '../Overlay';
import { DefaultDark, DefaultLight } from 'survey-core/themes';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
  exceedsSurveyFileCountLimit,
  getSurveyFileCount,
  getSurveyFileCountLimitError,
  getSurveyFileCountLimits,
} from './surveyFileLimits';
import {
  cloneSurveyData,
  completeSurveyFileChange,
  getJsonDiffShallow,
  getPersistableSurveyData,
  SurveySaveCoordinator,
} from './surveySaveCoordinator';
import { submitSurveyWithDeadline, writeSurvey } from './surveyWrite';
import { getSurveySubmissionErrorKey } from '../survey-runtime/surveySubmissionFailure';
import { buildSurveyErrorSupportMailto } from './surveyErrorEmail';
import SurveyLoadError from '../survey-runtime/SurveyLoadErrorScreen';
import { getSurveyLoadErrorKey, isSurveyCheckInRequired } from '../survey-runtime/surveyLoadFailure';
import { withTimeout } from '../../../helpers/withTimeout';
import { getApiErrorStatus as getSurveyErrorStatus } from '../../../helpers/apiErrorHelper';
import {
  flushActiveSurveyTextInput,
  getSurveyTextUpdateMode,
  shouldUseBlurSurveyTextUpdates,
} from './surveyTextInput';
import {
  advanceSurvey,
  getSurveyProgress,
  getSurveyNavigationState,
  restoreSurveyNavigation,
  type SurveyNavigationState,
} from './surveyNavigation';
import { areSurveyRendererPropsEqual } from './surveyRenderIsolation';
import {
  canShowSurveySideNavigation,
  getSurveySideNavigationOffset,
  SURVEY_CONTENT_WIDTH,
} from './surveyLayout';

let formVersionId: string | undefined = undefined;
const getSkipSurveyFetchOnceMissionId = () => (globalThis as any).__skipSurveyFetchOnceMissionAssignationId as string | undefined;
const clearSkipSurveyFetchOnceMissionId = () => {
  delete (globalThis as any).__skipSurveyFetchOnceMissionAssignationId;
};
type SurveyRendererProps = {
  model: SurveyModel;
};

const SurveyRenderer = React.memo(
  ({ model }: SurveyRendererProps) => <Survey model={model} />,
  areSurveyRendererPropsEqual,
);
SurveyRenderer.displayName = 'SurveyRenderer';

const SurveyJs = (params) => {
  const queryClient = useQueryClient();
  const t = useTranslation();
  const tMission = useTranslation({ defaultPath: 'mission' });
  const insets = useSafeAreaInsets();

  const getSurveyLoadBlockedMessage = (error: any) => {
    const key = getSurveyLoadErrorKey(error);
    return key ? t(key) : undefined;
  };

  const routeParams = params.route.params ?? {};
  const locationSearchParams = useMemo(() => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') {
      return undefined;
    }

    const searchParams = new URLSearchParams(document.location.search);

    return {
      webview: searchParams.get('webview'),
      missionAssignationId: searchParams.get('missionAssignationId'),
      lang: searchParams.get('lang'),
      token: searchParams.get('token'),
    };
  }, [params.route.params]);

  const dimension = useWindowDimensions();
  const theme = useCustomTheme();
  const authStore = useAppSelector((selector) => selector.auth);
  const completionSessionVersion = useAppSelector((selector) => selector.missions.completionSessionVersion);
  const apiMissions = useApi(MyMissionClient);
  const apiSurveyImages = useApi(SurveyImagesClient);
  const dispatch = useAppDispatch();
  const navigation = useNavigation();
  const isWebview = routeParams?.webview === true || routeParams?.webview === 'true' || locationSearchParams?.webview === 'true';
  const shouldUseBlurTextUpdates = shouldUseBlurSurveyTextUpdates(isWebview);
  const id = routeParams?.missionAssignationId ?? locationSearchParams?.missionAssignationId;
  const lang = routeParams?.lang ?? locationSearchParams?.lang;
  const incomingToken = routeParams?.token ?? locationSearchParams?.token;
  const userPrefs = useAppSelector((selector) => selector.userPrefs);
  const isDarkMode = userPrefs.theme === DeviceTheme.Dark;
  const bottomInset = Math.max(insets.bottom, 0);
  const surveyContentBackgroundColor = isDarkMode ? '#22252b' : (theme.background.primary as any);
  const surveyInputIconColor = isDarkMode ? '#ffffff' : '#000000';
  const surveyLoadingBackgroundColor = isDarkMode ? 'rgba(34, 37, 43, 0.92)' : 'rgba(255, 255, 255, 0.10)';
  // const styles = useStyles((selector) => selector.surveyJs);
  const styles = useStyles((section) => section.myMissions);

  const defaultLogo = <FontAwesomeIcon icon={faStore} style={{ padding: 12, color: theme.text.titleLight.toString(), width: '100%', height: '100%' }} />;
  const [logo, setLogo] = useState<ReactNode>(defaultLogo);
  const [missionSurvey, setMissionSurvey] = useState<GetSurveyFromMissionAssignationViewModel>();
  const [surveyAnswers, setSurveyAnswers] = useState<PartialSaveMissionAnswerRequest[]>();
  const [savedSurveyJsAnswers, setSavedSurveyJsAnswers] = useState<any[]>();
  const [currentMission, setCurrentMission] = useState<SurveyFormModel>();

  const [survey, setSurvey] = useState<SurveyModel>(undefined);
  const [surveyNavigationState, setSurveyNavigationState] = React.useState<SurveyNavigationState>();
  const currentPageNoRef = React.useRef(0);
  const currentElementNameRef = React.useRef<string | undefined>(undefined);
  const preservePageOnSurveyRebuildRef = React.useRef(false);
  const [hideActionBar, setHideActionBar] = useState(false);
  const [querySeed, setQuerySeed] = useState<string>();
  const [nextNav, setNextNav] = useState(false);

  const [surveyCompleted, setSurveyCompleted] = useState(false);
  const [surveyError, setSurveyError] = useState<any>();
  const [surveyLoadBlockedMessage, setSurveyLoadBlockedMessage] = useState<string>();
  const [loading, setLoading] = useState(true);
  const [overlayLoading, setOverlayLoading] = useState(false);
  const [triggerSurveyJs, setTriggerSurveyJs] = useState<boolean>(false);
  const [prevWidth, setPrevWidth] = useState(dimension.width);
  const partialSaveTimerRef = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const saveCoordinatorRef = React.useRef<SurveySaveCoordinator | undefined>(undefined);
  const surveyAnswersRef = React.useRef<PartialSaveMissionAnswerRequest[] | undefined>(undefined);
  const savedSurveyJsAnswersRef = React.useRef<any[] | undefined>(undefined);
  const draftSurveyJsAnswersRef = React.useRef<any[] | undefined>(undefined);
  const currentMissionRef = React.useRef<SurveyFormModel | undefined>(undefined);
  const submissionRef = React.useRef<Promise<unknown> | undefined>(undefined);

  if (!saveCoordinatorRef.current) {
    saveCoordinatorRef.current = new SurveySaveCoordinator();
  }

  const isInjectedTokenReady = !incomingToken || authStore.idToken === incomingToken;
  const shouldSkipInitialSurveyFetch = !!id && getSkipSurveyFetchOnceMissionId() === id;

  const syncSurveyNavigationState = (activeSurvey: SurveyModel) => {
    const nextState = getSurveyNavigationState(activeSurvey);
    currentPageNoRef.current = nextState.currentPageNo;
    currentElementNameRef.current = nextState.currentElementName;
    setSurveyNavigationState(nextState);
  };

  const resetSurveyNavigationState = () => {
    currentPageNoRef.current = 0;
    currentElementNameRef.current = undefined;
    setSurveyNavigationState(undefined);
  };

  const goToMyMissionsList = () => {
    if (isWebview && typeof window !== 'undefined' && (window as any).ReactNativeWebView?.postMessage) {
      (window as any).ReactNativeWebView.postMessage('back');
    } else {
      navigation.navigate('mymissions' as never);
    }
  };

  useEffect(() => {
    if ((prevWidth < 617 && dimension.width >= 617) || (prevWidth >= 617 && dimension.width < 617)) {
      preservePageOnSurveyRebuildRef.current = true;
      setTriggerSurveyJs((prev) => !prev);
    }
    setPrevWidth(dimension.width);
  }, [dimension.width, prevWidth]);

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') {
      return;
    }

    const calendarIconColorHex = isDarkMode ? 'ffffff' : '000000';
    const calendarIconDataUri = `url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23${calendarIconColorHex}'%3E%3Cpath d='M7 2h2v2h6V2h2v2h3a1 1 0 0 1 1 1v15a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a1 1 0 0 1 1-1h3V2zm12 8H5v10h14V10zM5 8h14V6H5v2z'/%3E%3C/svg%3E\")`;
    const clockIconDataUri = `url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23${calendarIconColorHex}'%3E%3Cpath d='M12 1C5.925 1 1 5.925 1 12s4.925 11 11 11 11-4.925 11-11S18.075 1 12 1zm0 20c-4.963 0-9-4.037-9-9s4.037-9 9-9 9 4.037 9 9-4.037 9-9 9zm1-14h-2v6.414l4.293 4.293 1.414-1.414L13 12.586V7z'/%3E%3C/svg%3E\")`;

    const styleId = 'survey-input-icon-theme-overrides';
    let styleEl = document.getElementById(styleId) as HTMLStyleElement | null;

    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = styleId;
      document.head.appendChild(styleEl);
    }

    styleEl.innerHTML = `
      .sd-root-modern input[type="date"],
      .sd-root-modern input[type="datetime-local"],
      .sd-root-modern input[type="time"],
      .sd-root-modern input[type="month"],
      .sd-root-modern input[type="week"],
      input.sd-input[type="date"],
      input.sd-input[type="datetime-local"],
      input.sd-input[type="time"],
      input.sd-input[type="month"],
      input.sd-input[type="week"] {
        color-scheme: ${isDarkMode ? 'dark' : 'light'} !important;
        color: ${surveyInputIconColor} !important;
      }

      .sd-root-modern input[type="date"]::-webkit-datetime-edit,
      .sd-root-modern input[type="date"]::-webkit-datetime-edit-text,
      .sd-root-modern input[type="date"]::-webkit-datetime-edit-year-field,
      .sd-root-modern input[type="date"]::-webkit-datetime-edit-month-field,
      .sd-root-modern input[type="date"]::-webkit-datetime-edit-day-field,
      .sd-root-modern input[type="datetime-local"]::-webkit-datetime-edit,
      .sd-root-modern input[type="datetime-local"]::-webkit-datetime-edit-text,
      .sd-root-modern input[type="datetime-local"]::-webkit-datetime-edit-year-field,
      .sd-root-modern input[type="datetime-local"]::-webkit-datetime-edit-month-field,
      .sd-root-modern input[type="datetime-local"]::-webkit-datetime-edit-day-field {
        color: ${surveyInputIconColor} !important;
      }

      .sd-root-modern input[type="date"]::-webkit-calendar-picker-indicator,
      .sd-root-modern input[type="datetime-local"]::-webkit-calendar-picker-indicator,
      input.sd-input[type="date"]::-webkit-calendar-picker-indicator,
      input.sd-input[type="datetime-local"]::-webkit-calendar-picker-indicator {
        -webkit-appearance: none !important;
        appearance: none !important;
        width: 16px !important;
        height: 16px !important;
        min-width: 16px !important;
        min-height: 16px !important;
        padding: 0 !important;
        margin: 0 !important;
        cursor: pointer !important;
        background-image: ${calendarIconDataUri} !important;
        background-repeat: no-repeat !important;
        background-position: center !important;
        background-size: 14px 14px !important;
        color: ${surveyInputIconColor} !important;
        opacity: 1 !important;
      }

      .sd-root-modern input[type="time"]::-webkit-calendar-picker-indicator,
      input.sd-input[type="time"]::-webkit-calendar-picker-indicator {
        -webkit-appearance: none !important;
        appearance: none !important;
        width: 16px !important;
        height: 16px !important;
        min-width: 16px !important;
        min-height: 16px !important;
        padding: 0 !important;
        margin: 0 !important;
        cursor: pointer !important;
        background-image: ${clockIconDataUri} !important;
        background-repeat: no-repeat !important;
        background-position: center !important;
        background-size: 14px 14px !important;
        color: ${surveyInputIconColor} !important;
        opacity: 1 !important;
      }

      .sd-root-modern input[type="month"]::-webkit-calendar-picker-indicator,
      .sd-root-modern input[type="week"]::-webkit-calendar-picker-indicator,
      input.sd-input[type="month"]::-webkit-calendar-picker-indicator,
      input.sd-input[type="week"]::-webkit-calendar-picker-indicator {
        ${isDarkMode ? 'filter: invert(1) brightness(2) contrast(2) !important;' : 'filter: invert(0) brightness(0) !important;'}
        opacity: 1 !important;
      }
    `;
  }, [surveyInputIconColor, isDarkMode]);

  useMemo(() => {
    setQuerySeed(StringHelper.generateRandomString());
    setHideActionBar(false);
    setSurveyCompleted(false);
  }, []);

  useFocusEffect(
    React.useCallback(() => {
      dispatch(navBarActions.setVisibility(false));
    }, [])
  );

  useEffect(() => {
    if (lang) {
      dispatch(userPrefsActions.setUiLanguageAndLanguages(lang));
    }
  }, [lang]);

  useEffect(() => {
    dispatch(navBarActions.setIsWebView(!!isWebview));

    return () => {
      dispatch(navBarActions.setIsWebView(false));
    };
  }, [dispatch, isWebview]);

  useEffect(() => {
    currentMissionRef.current = currentMission;
  }, [currentMission]);

  useEffect(() => {
    surveyAnswersRef.current = surveyAnswers;
  }, [surveyAnswers]);

  useEffect(() => {
    savedSurveyJsAnswersRef.current = savedSurveyJsAnswers;
  }, [savedSurveyJsAnswers]);

  type SurveySaveSnapshot = {
    formVersionId: string;
    currentMissionFormVersionId: string;
    data: Record<string, any>;
    pageNo: number;
    currentElementName?: string;
  };

  const clearPendingPartialSave = () => {
    if (partialSaveTimerRef.current) {
      clearTimeout(partialSaveTimerRef.current);
      partialSaveTimerRef.current = undefined;
    }
  };

  const updateDraftSnapshot = (snapshot: SurveySaveSnapshot) => {
    const answers = surveyAnswersRef.current ?? [];
    const answerIndex = answers.findIndex((answer) => answer.formVersionId === snapshot.formVersionId);

    if (answerIndex < 0) {
      return;
    }

    const drafts = [...(draftSurveyJsAnswersRef.current ?? [])];
    const savedAnswers = savedSurveyJsAnswersRef.current ?? [];
    drafts[answerIndex] = getPersistableSurveyData(snapshot.data, savedAnswers[answerIndex] ?? {});
    draftSurveyJsAnswersRef.current = drafts;
  };

  const parseMetadata = (metadataJson?: string) => {
    if (!metadataJson) {
      return {};
    }

    try {
      return JSON.parse(metadataJson);
    } catch {
      return {};
    }
  };

  const buildPartialSavePayload = (snapshot: SurveySaveSnapshot) => {
    const answers = surveyAnswersRef.current ?? [];
    const savedAnswers = savedSurveyJsAnswersRef.current ?? [];
    const draftsAtRequest = (draftSurveyJsAnswersRef.current ?? []).map((draft) => cloneSurveyData(draft ?? {}));
    const currentAnswerIndex = answers.findIndex((answer) => answer.formVersionId === snapshot.formVersionId);

    const requests = answers.map((request, index) => {
      const metadata = parseMetadata(request.metadataJson);

      if (index === currentAnswerIndex) {
        metadata.currentPageNo = snapshot.pageNo;
        metadata.currentElementName = snapshot.currentElementName;
      }

      if (index === 0) {
        metadata.currentMission = snapshot.currentMissionFormVersionId;
      }

      return PartialSaveMissionAnswerRequest.fromJS({
        ...request.toJSON(),
        metadataJson: JSON.stringify(metadata),
        answer: {
          ...request.answer?.toJSON(),
          answerJson: JSON.stringify(getJsonDiffShallow(draftsAtRequest[index] ?? {}, savedAnswers[index] ?? {})),
        },
      });
    });

    surveyAnswersRef.current = requests;
    setSurveyAnswers(requests);

    return { requests, draftsAtRequest, currentAnswerIndex };
  };

  const persistDraft = async (snapshot: SurveySaveSnapshot, token?: CancelToken) => {
    const { requests, draftsAtRequest, currentAnswerIndex } = buildPartialSavePayload(snapshot);

    if (!requests.length || currentAnswerIndex < 0) {
      return false;
    }

    setOverlayLoading(approximateStringSizeInKB(requests[currentAnswerIndex].answer?.answerJson) > 500);

    try {
      const save = async (requestToken: CancelToken) => {
        const saved = await apiMissions.save(id, new Date().getTimezoneOffset(), requests, requestToken);
        requestToken.throwIfRequested();
        if (saved) {
          savedSurveyJsAnswersRef.current = draftsAtRequest;
          setSavedSurveyJsAnswers(draftsAtRequest);
        }
        return saved;
      };
      return await (token ? save(token) : writeSurvey(save));
    } finally {
      setOverlayLoading(false);
    }
  };

  const queuePartialSave = (snapshot: SurveySaveSnapshot) => {
    return saveCoordinatorRef.current!.enqueue(() => persistDraft(snapshot)).catch(() => {
      // Keep the last confirmed baseline. The next autosave or final submit
      // will resend every unsaved answer from the in-memory draft.
      return false;
    });
  };

  const buildSubmitPayload = () => {
    const answers = surveyAnswersRef.current ?? [];
    const savedAnswers = savedSurveyJsAnswersRef.current ?? [];
    const drafts = draftSurveyJsAnswersRef.current ?? [];

    return answers.map((request, index) =>
      SaveMissionAnswerDto.fromJS({
        ...request.answer?.toJSON(),
        answerJson: JSON.stringify(getJsonDiffShallow(drafts[index] ?? {}, savedAnswers[index] ?? {})),
      }),
    );
  };

  const getCompletionPrompt = (): MissionCompletionPrompt | undefined => !isWebview && !getCircuitSurveyContext(missionSurvey) && authStore.user?.uid ? {
    missionId: id, userId: authStore.user.uid, sessionVersion: completionSessionVersion,
  } : undefined;

  const apiMissionsMutation = useMutation({
    retry: false,
    networkMode: 'always',
    useErrorBoundary: false,
    mutationFn: async (data: { snapshot: SurveySaveSnapshot; completionPrompt?: MissionCompletionPrompt }) => {
      if (submissionRef.current) return submissionRef.current;
      setLoading(true);
      setSurveyError(undefined);
      clearPendingPartialSave();
      updateDraftSnapshot(data.snapshot);

      const attempt = submitSurveyWithDeadline(
        saveCoordinatorRef.current!,
        // Persist the complete draft before changing mission state. If the
        // completion response is lost, reopening the survey restores every
        // answer, including the last comment entered on a one-page survey.
        (token) => persistDraft(data.snapshot, token),
        // If the draft write failed, submit the diff from the last confirmed
        // baseline so retry still includes the complete in-memory draft.
        (token) => apiMissions.saveAndComplete(id, new Date().getTimezoneOffset(), buildSubmitPayload(), token),
      );
      submissionRef.current = attempt;
      try {
        return await attempt;
      } finally {
        submissionRef.current = undefined;
      }
    },
    onSuccess(_result, { completionPrompt }) {
      if (completionPrompt) dispatch(missionsActions.queueMissionCompletionPrompt(completionPrompt));
      setLoading(false);
      dispatch(missionsActions.setMissionAcceptedId(getCircuitSurveyContext(missionSurvey)?.mainMissionAssignationId ?? id));
      setSurveyCompleted(true);
      dispatch(missionsActions.setReloadMissionListSeed(StringHelper.generateRandomString()));
      queryClient.invalidateQueries({ queryKey: ['competitor-circuit'] });
    },
    onError(error: any) {
      // Changing here
      setLoading(false);
      setSurveyError(error);
    },
  });

  const submitSurvey = (enteredSurvey: SurveyModel, options) => {
    clearPendingPartialSave();

    const activeMission = currentMissionRef.current;
    if (!activeMission) {
      return;
    }

    const snapshot: SurveySaveSnapshot = {
      formVersionId: activeMission.formVersionId,
      currentMissionFormVersionId: activeMission.nextForm?.formVersionId ?? activeMission.formVersionId,
      data: cloneSurveyData(enteredSurvey.data ?? {}),
      pageNo: enteredSurvey.currentPageNo,
      currentElementName: enteredSurvey.currentElementName || undefined,
    };
    updateDraftSnapshot(snapshot);

    if (!activeMission.nextForm) {
      setHideActionBar(true);
      apiMissionsMutation.mutate({ snapshot, completionPrompt: getCompletionPrompt() });
    } else {
      queuePartialSave(snapshot);
      currentMissionRef.current = activeMission.nextForm;
      setCurrentMission(activeMission.nextForm);
    }
  };

  const approximateStringSizeInKB = (str?: string) => {
    return str?.length / 1024.0;
  };

  const createSurveySaveSnapshot = (survey: SurveyModel, activeFormVersionId: string): SurveySaveSnapshot => ({
    formVersionId: activeFormVersionId,
    currentMissionFormVersionId: currentMissionRef.current?.formVersionId ?? activeFormVersionId,
    data: cloneSurveyData(survey.data ?? {}),
    pageNo: survey.currentPageNo,
    currentElementName: survey.currentElementName || undefined,
  });

  const saveSurveyImmediately = (survey: SurveyModel, activeFormVersionId: string) => {
    const snapshot = createSurveySaveSnapshot(survey, activeFormVersionId);
    updateDraftSnapshot(snapshot);
    clearPendingPartialSave();

    if (params.route.name != 'survey') {
      return Promise.resolve(false);
    }

    return queuePartialSave(snapshot);
  };

  const saveSurvey = (survey: SurveyModel, options: any, activeFormVersionId: string, delay = 1500) => {
    if (delay <= 0) {
      void saveSurveyImmediately(survey, activeFormVersionId);
      return;
    }

    const snapshot = createSurveySaveSnapshot(survey, activeFormVersionId);
    updateDraftSnapshot(snapshot);
    clearPendingPartialSave();

    if (params.route.name == 'survey') {
      partialSaveTimerRef.current = setTimeout(() => {
        partialSaveTimerRef.current = undefined;
        queuePartialSave(snapshot);
      }, delay);
    }
  };

  useEffect(() => () => clearPendingPartialSave(), []);

  useEffect(() => {
    if (incomingToken && authStore.idToken !== incomingToken) {
      dispatch(authActions.setToken(incomingToken));
    }
  }, [incomingToken, authStore.idToken, dispatch]);

  useEffect(() => {
    if (!shouldSkipInitialSurveyFetch) {
      return;
    }

    setLoading(true);

    if (isWebview && typeof window !== 'undefined' && (window as any).ReactNativeWebView?.postMessage) {
      (window as any).ReactNativeWebView.postMessage('error');
    } else {
      navigation.navigate('mymissions' as never);
    }

    return () => {
      clearSkipSurveyFetchOnceMissionId();
    };
  }, [shouldSkipInitialSurveyFetch, isWebview, navigation]);

  const surveyQuery = useQuery({
    enabled: !!authStore.idToken && !!querySeed && !!id && isInjectedTokenReady && !shouldSkipInitialSurveyFetch,
    queryKey: ['surveyQuery', querySeed, userPrefs.uiLanguage, id],
    retry: false,
    networkMode: 'always',
    useErrorBoundary: false,
    refetchOnReconnect: false,
    queryFn: () => {
      setLoading(true);
      setSurveyLoadBlockedMessage(undefined);
      return withTimeout(apiMissions.getMissionSurvey(id), 20000).catch((error) => {
        const blockedMessage = getSurveyLoadBlockedMessage(error);

        if (blockedMessage) {
          setLoading(false);
          setSurveyLoadBlockedMessage(blockedMessage);
          // Do not clear in-memory answers on a failed read.
          if (isSurveyCheckInRequired(error) && !isWebview) {
            navigation.navigate({ name: 'mymissions', params: { id } } as never);
          }
        }

        throw error;
      });
    },
  });
  const surveyLoadBlockedMessageResolved = surveyQuery.isFetching ? undefined : surveyLoadBlockedMessage ?? getSurveyLoadBlockedMessage(surveyQuery.error);

  const showFileTypeError = () => {
    dispatch(
      lightBoxActions.setContent(
        <ProfileModal hideConfirm>
          <View style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <FontAwesomeIcon icon={faTriangleExclamation} size={48} color={theme.colorErrorPrimary.toString()} />
            <Label style={{ marginTop: 8 }}>{t('Please-upload-only-PNG-or-JPEG-files')}</Label>
          </View>
        </ProfileModal>
      )
    );
    dispatch(lightBoxActions.setVisibility(true));
  };

  useEffect(() => {
    const buildPartialSave = (form: SurveyFormModel) => {
      if (!form) {
        return;
      }

      var payload = new PartialSaveMissionAnswerRequest();
      payload.formVersionId = form.formVersionId;
      payload.metadataJson = form.metadataJson;
      payload.answer = new SaveMissionAnswerDto();
      payload.answer.answerJson = form.formAnswerJson;
      payload.answer.missionAssignationId = form.missionAssignationId;
      payload.answer.periodMissionConfigurationId = form.periodMissionConfigurationId;
      payload.answer.rootMissionAssignationId = id;

      if (form.nextForm) {
        return [payload].concat(buildPartialSave(form.nextForm));
      }

      return [payload];
    };

    if (surveyQuery.isSuccess && surveyQuery.data) {
      setSurveyLoadBlockedMessage(undefined);
      setMissionSurvey(surveyQuery.data);
      var answers = buildPartialSave(surveyQuery.data.survey);
      const restoredAnswers = answers.map((x: PartialSaveMissionAnswerRequest) => JSON.parse(x.answer.answerJson ?? '{}'));
      surveyAnswersRef.current = answers;
      savedSurveyJsAnswersRef.current = restoredAnswers;
      draftSurveyJsAnswersRef.current = restoredAnswers.map((answer) => cloneSurveyData(answer));
      setSavedSurveyJsAnswers(restoredAnswers);
      setSurveyAnswers(answers);

      const meta = answers?.length ? parseMetadata(answers[0].metadataJson) : undefined;
      const restoredFormVersionId = meta?.currentMission;
      let restoredMission = surveyQuery.data.survey;
      let candidateMission = surveyQuery.data.survey;

      while (candidateMission) {
        if (candidateMission.formVersionId === restoredFormVersionId) {
          restoredMission = candidateMission;
          break;
        }

        candidateMission = candidateMission.nextForm;
      }

      currentMissionRef.current = restoredMission;
      setCurrentMission(restoredMission);
    }

    if (surveyQuery.isError) {
      const blockedMessage = getSurveyLoadBlockedMessage(surveyQuery.error);

      if (blockedMessage) {
        setLoading(false);
        setSurveyLoadBlockedMessage(blockedMessage);
        setMissionSurvey(undefined);
        setSurveyAnswers(undefined);
        setSavedSurveyJsAnswers(undefined);
        surveyAnswersRef.current = undefined;
        savedSurveyJsAnswersRef.current = undefined;
        draftSurveyJsAnswersRef.current = undefined;
        setCurrentMission(undefined);
        currentMissionRef.current = undefined;
        setSurvey(undefined);
        setSurveyCompleted(false);
        setHideActionBar(false);
      }
    }

    dispatch(navBarActions.setVisibility(false));
  }, [surveyQuery.isSuccess, surveyQuery.isError, surveyQuery.data, surveyQuery.error, isWebview, navigation]);

  const showImageZoom = (imageSrc) => {
    dispatch(
      lightBoxActions.setContent(
        <View style={{ display: 'flex', minHeight: '100%' }}>
          <ImageZoom
            width={dimension.width > dimension.height ? undefined : dimension.width - 40}
            height={dimension.width > dimension.height ? dimension.height - 40 : undefined}
            alt=""
            src={imageSrc}
            zoom="200"
          />
        </View>
      )
    );
    dispatch(lightBoxActions.setVisibility(true));
  };

  const compressBlob = (blob: Blob) => {
    return new Promise<Blob>((resolve, reject) => {
      new Compressor(blob, {
        quality: 0.6,
        success: resolve,
        error: reject,
      });
    });
  };

  const isSupportedSurveyFile = (file: File) => {
    const fileType = file.type?.toLowerCase();
    const fileName = file.name?.toLowerCase();

    return fileType === 'image/png' || fileType === 'image/jpeg' || fileType === 'image/jpg' || fileType === 'application/pdf' || fileName?.endsWith('.pdf');
  };

  const getSurveyImageId = (content?: string) => {
    const match = content?.match(/\/api\/ms\/client\/survey-images\/([0-9a-f-]{36})(?:$|\?)/i);
    return match?.[1];
  };

  const toSurveyUploadResult = (file: File, response: any) => ({
    file,
    content: response?.content ?? response?.Content ?? response?.url ?? response?.Url,
  });

  const uploadSurveyImage = async (file: File) => {
    const sourceBlob = file.type?.toLowerCase().startsWith('image/') ? await compressBlob(file) : file;
    const uploadFile =
      sourceBlob instanceof File
        ? sourceBlob
        : new File([sourceBlob], file.name, {
            type: sourceBlob.type || file.type,
          });

    // The generated client currently expands IFormFile into metadata fields
    // instead of accepting the file body. Build the multipart request here so
    // the blob is actually sent, while still using the client's authenticated
    // request pipeline and native FormData normalization.
    const formData = new FormData();
    formData.append('file', uploadFile);

    const baseUrl = (apiSurveyImages as any).baseUrl ?? '';
    const options = await (apiSurveyImages as any).transformOptions({
      method: 'POST',
      url: `${baseUrl}/api/ms/client/survey-images`,
      data: formData,
      headers: { Accept: 'application/json' },
      cancelToken: apiSurveyImages.tokenSource?.token,
    });
    const response = await (apiSurveyImages as any).instance.request(options);

    return toSurveyUploadResult(file, response.data);
  };

  const fileToSurveyValue = async (file: File) => {
    if (!isSupportedSurveyFile(file)) {
      throw new Error('Unsupported survey file type.');
    }

    return uploadSurveyImage(file);
  };

  const deleteSurveyImage = async (content?: string) => {
    const imageId = getSurveyImageId(content);
    const missionAssignationId = currentMission?.missionAssignationId;
    if (!imageId || !missionAssignationId) {
      return;
    }

    await apiSurveyImages.delete(imageId, missionAssignationId, undefined, apiSurveyImages.tokenSource?.token);
  };

  useEffect(() => {
    if (currentMission) {
      const surveyJson = JSON.parse(currentMission.formJson);
      const fileCountLimits = getSurveyFileCountLimits(surveyJson);
      const newSurvey = new SurveyModel(surveyJson);
      newSurvey.onDownloadFile.add((_, options) => {
        options.callback('success', resolveSurveyFileUrl(options.content, (apiSurveyImages as any).baseUrl ?? ''));
      });
      // SurveyJS 1.x rendered the desktop questionnaire in its fixed-width body.
      // Keep that layout contract after the 2.x upgrade while allowing the root-level
      // progress indicator to continue spanning the available width.
      newSurvey.widthMode = 'static';
      newSurvey.applyTheme(isDarkMode ? DefaultDark : DefaultLight);
      newSurvey.getAllQuestions().forEach((question) => {
        if (question instanceof QuestionFileModel) {
          question.storeDataAsText = false;
        }
      });

      // Making the survey as 1 question per page
      if (!newSurvey.questionsOnPageMode || newSurvey.questionsOnPageMode !== 'questionPerPage') {
        newSurvey.questionsOnPageMode = 'questionPerPage';
      }

      if (newSurvey.progressBarType === 'pages') {
        if (!ReactElementFactory.Instance.isElementRegistered('sv-progressbar-percentage')) {
          ReactElementFactory.Instance.registerElement('sv-progressbar-percentage', (props) => {
            return React.createElement(PercentageProgressBar, props);
          });
        }

        newSurvey.addLayoutElement({
          id: 'progressbar-percentage',
          component: 'sv-progressbar-percentage',
          container: 'contentTop',
          data: {
            survey: newSurvey,
            chunk: 100 / surveyAnswers.length,
            floorPercentage: () => {
              return surveyAnswers.findIndex((a) => a.formVersionId === currentMission.formVersionId) * (100 / surveyAnswers.length);
            },
          },
        });
      }

      newSurvey.title = '';

      newSurvey.textUpdateMode = getSurveyTextUpdateMode(shouldUseBlurTextUpdates);
      newSurvey.showNavigationButtons = 'none';
      newSurvey.showQuestionNumbers = 'off';

      newSurvey.locale = missionSurvey.restrictedLanguage ?? userPrefs.uiLanguage;
      formVersionId = currentMission.formVersionId;

      newSurvey.onAfterRenderQuestion.add(function (s, options) {
        options.htmlElement.querySelectorAll('.sd-element__num').forEach((el) => {
          (el as HTMLElement).style.fontSize = '16px';
          (el as HTMLElement).style.color = isDarkMode ? '#e5edf4' : 'black';
          (el as HTMLElement).style.padding = '8 6 0 0';
          (el as HTMLElement).style.lineHeight = '15px';
        });

        if (dimension.width >= 617) {
          options.htmlElement.querySelectorAll('.sd-matrix tbody, .sd-matrix tr, .sd-matrix td').forEach((el) => {
            (el as HTMLElement).style.display = 'block';
            (el as HTMLElement).style.width = '100%';
            (el as HTMLElement).style.textAlign = 'left';
            (el as HTMLElement).style.border = '1px';
          });

          options.htmlElement.querySelectorAll('.sd-matrix .sd-matrix__label').forEach((el) => {
            (el as HTMLElement).style.justifyContent = 'start';
          });

          options.htmlElement.querySelectorAll('.sd-matrix thead').forEach((row) => {
            (row as HTMLElement).style.display = 'none';
          });
          const headerValues = [];
          options.htmlElement.querySelectorAll('.sd-matrix th').forEach((header) => {
            headerValues.push((header as HTMLElement).textContent);
          });

          const labels = options.htmlElement.querySelectorAll('.sd-matrix .sd-matrix__label');
          labels.forEach((label, i) => {
            const valueToAdd = headerValues[i % headerValues.length] || '';

            const span = document.createElement('span');
            span.textContent = valueToAdd;
            span.style.marginLeft = '15px';
            (label as HTMLElement).style.paddingTop = '5px';
            (label as HTMLElement).style.paddingBottom = '5px';
            (label as HTMLElement).appendChild(span);
          });
        }

        if (options.question instanceof QuestionFileModel) {
          const questionElement = options.htmlElement as HTMLElement;
          initializeSurveyFileInput(options.question, questionElement);

          questionElement.querySelectorAll<HTMLImageElement>('.sd-file__preview-item img').forEach((imageElement) => {
            imageElement.style.cursor = 'pointer';
          });

          if (questionElement.dataset.filePreviewClickHandled !== 'true') {
            questionElement.dataset.filePreviewClickHandled = 'true';
            questionElement.addEventListener('click', (event) => {
              if (!(event.target instanceof Element)) {
                return;
              }

              const target = event.target;
              const previewTrigger = target.closest<HTMLImageElement | HTMLAnchorElement>('.sd-file__preview-item img, .sd-file__sign a');

              if (!previewTrigger || !questionElement.contains(previewTrigger)) {
                return;
              }

              const previewElement = previewTrigger.closest<HTMLElement>('.sd-file__preview-item');
              const source =
                previewElement?.querySelector<HTMLImageElement>('img')?.src
                || previewElement?.querySelector<HTMLAnchorElement>('.sd-file__sign a')?.href;

              if (!source) {
                return;
              }

              event.preventDefault();
              event.stopPropagation();

              if (previewElement?.querySelector('img')) {
                showImageZoom(source);
              } else {
                window.open(source, '_blank', 'noopener,noreferrer');
              }
            }, true);
          }
        }
      });

      newSurvey.onComplete.add(submitSurvey);

      newSurvey.onUploadFiles.add(async (_, options: any) => {
        const selectedFiles = options.files ?? [];
        const maxFiles = fileCountLimits.get(options.name);
        const currentFileCount = getSurveyFileCount(options.question?.value);

        if (exceedsSurveyFileCountLimit(currentFileCount, selectedFiles.length, maxFiles)) {
          options.callback('error', getSurveyFileCountLimitError(maxFiles));
          return;
        }

        if (selectedFiles.some((file: File) => !isSupportedSurveyFile(file))) {
          showFileTypeError();
          options.callback('error');
          return;
        }

        try {
          setOverlayLoading(true);
          const files = await Promise.all(selectedFiles.map((file: File) => fileToSurveyValue(file)));
          await completeSurveyFileChange(
            () => options.callback('success', files),
            () => saveSurveyImmediately(newSurvey, currentMission.formVersionId),
          );
        } catch {
          options.callback('error');
        } finally {
          setOverlayLoading(false);
        }
      });

      newSurvey.onClearFiles.add(async (_, options: any) => {
        const values = Array.isArray(options.value) ? options.value : options.value ? [options.value] : [];

        try {
          await Promise.all(values.map((value: { content?: string }) => deleteSurveyImage(value.content)));
          await completeSurveyFileChange(
            () => options.callback('success'),
            () => saveSurveyImmediately(newSurvey, currentMission.formVersionId),
          );
        } catch {
          options.callback('error');
        }
      });

      newSurvey.onValueChanged.add(async (s, question) => {
        if (question.question instanceof QuestionFileModel) {
          var file = question.value[0];
          if (file && !isSupportedSurveyFile(file)) {
            showFileTypeError();
            newSurvey.clearValue(question.name);
          }

          // File changes are saved by the upload/clear callbacks only after
          // SurveyJS has replaced its local preview with the backend URL.
          return;
        }

        saveSurvey(s, question, formVersionId, question.question instanceof QuestionCommentModel ? 3000 : 1500);
      });

      newSurvey.onCurrentPageChanged.add((s, o) => {
        syncSurveyNavigationState(s);
        saveSurvey(s, o, formVersionId, 0);
      });

      const converter = new showdown.Converter();
      newSurvey.onTextMarkdown.add(function (survey, options) {
        let str = converter.makeHtml(options.text).trim();

        if (str.startsWith('<p>')) {
          str = str.substring(3);
        }

        if (str.endsWith('</p>')) {
          str = str.substring(0, str.length - 4);
        }

        options.html = str;
      });

      syncSurveyNavigationState(newSurvey);
      setSurvey(newSurvey);
    }
  }, [currentMission, userPrefs.uiLanguage, userPrefs.theme, triggerSurveyJs, shouldUseBlurTextUpdates]);

  useEffect(() => {
    if (!!surveyAnswers) {
      var surveyAnswerIndex = surveyAnswers.findIndex((a) => a.formVersionId === currentMission.formVersionId);
      var current = surveyAnswers[surveyAnswerIndex];
      var currentAnswerJson = draftSurveyJsAnswersRef.current?.[surveyAnswerIndex]
        ?? savedSurveyJsAnswers[surveyAnswerIndex]
        ?? JSON.parse(current.answer.answerJson);

      if (survey) {
        let persistedNavigation: { currentElementName?: string; currentPageNo?: number } = {};

        if (preservePageOnSurveyRebuildRef.current) {
          persistedNavigation = {
            currentElementName: currentElementNameRef.current,
            currentPageNo: currentPageNoRef.current,
          };
          preservePageOnSurveyRebuildRef.current = false;
        } else if (current.metadataJson && !nextNav) {
          const metadata = parseMetadata(current.metadataJson);
          persistedNavigation = {
            currentElementName: metadata.currentElementName,
            currentPageNo: metadata.currentPageNo,
          };
        }

        if (currentAnswerJson && Object.keys(currentAnswerJson)?.length > 0) {
          survey.data = currentAnswerJson;
        }

        setSurveyNavigationState(restoreSurveyNavigation(survey, persistedNavigation));
        currentPageNoRef.current = survey.currentPageNo;
        currentElementNameRef.current = survey.currentElementName || undefined;
      } else {
        resetSurveyNavigationState();
      }

      if (survey) {
        setLoading(false);
      }
    } else {
      resetSurveyNavigationState();
    }
  }, [survey]);

  useEffect(() => {
    if (missionSurvey?.branchInfo?.logoUrl) {
      setLogo(<Image style={{ padding: 12, width: '100%', height: '100%' }} source={{ uri: missionSurvey.branchInfo?.logoUrl }} />);
    } else {
      setLogo(defaultLogo);
    }
  }, [missionSurvey?.branchInfo?.logoUrl]);

  const getEmptyComponent = () => {
    const circuitContext = getCircuitSurveyContext(missionSurvey);
    return circuitContext ? <CompetitorCircuitSurveyCompleted context={circuitContext} isWebview={isWebview} /> : <SurveyCompleted isWebview={isWebview} />;
  };

  const getSurveyBlockedComponent = () => {
    return (
      <SurveyLoadError
        message={surveyLoadBlockedMessageResolved}
        onRetry={() => void surveyQuery.refetch()}
        onBack={goToMyMissionsList}
      />
    );
  };

  const hasPreviousSurvey = () => {
    return !!missionSurvey?.survey && missionSurvey.survey != currentMission;
  };

  const getPreviousSurvey = (f: SurveyFormModel = missionSurvey?.survey) => {
    if (!f) {
      return undefined;
    }

    if (f.nextForm == currentMission) {
      return f;
    } else {
      return f.nextForm ? getPreviousSurvey(f.nextForm) : undefined;
    }
  };

  const goToPreviousSurveyPage = () => {
    flushActiveSurveyTextInput(shouldUseBlurTextUpdates);
    setNextNav(false);

    if (survey && !survey.isFirstPage) {
      survey?.prevPage();
    } else {
      setCurrentMission(getPreviousSurvey());
    }
  };

  const goToNextSurveyPage = () => {
    flushActiveSurveyTextInput(shouldUseBlurTextUpdates);
    setNextNav(true);

    if (survey) {
      advanceSurvey(survey);
    }
  };

  const isFirstSurveyStep = surveyNavigationState?.isFirstStep ?? true;
  const isLastSurveyStep = surveyNavigationState?.isLastStep ?? false;
  const showSideNavigation = canShowSurveySideNavigation(dimension.width);
  const sideNavigationOffset = getSurveySideNavigationOffset(dimension.width);
  const showMobileActionBar = !surveyError && !surveyLoadBlockedMessageResolved && !surveyCompleted && !hideActionBar && !showSideNavigation;
  const mobileActionBarHeight = showMobileActionBar ? 80 + bottomInset : 0;

  return (
    <>
      {loading && !surveyLoadBlockedMessageResolved && (
        <Loading style={{ backgroundColor: surveyLoadingBackgroundColor }} iconColor={isDarkMode ? '#ffffff' : undefined} />
      )}
      {(!loading || !!surveyLoadBlockedMessageResolved) && !shouldSkipInitialSurveyFetch && (
        <View style={{ flex: 1, backgroundColor: surveyContentBackgroundColor, position: 'relative' }}>
          <Modal animationType="none" visible={overlayLoading} transparent={true}>
            <Overlay showOverLay={true} zIndex={3000}>
              <Loading style={{ backgroundColor: surveyLoadingBackgroundColor }} iconColor={isDarkMode ? '#ffffff' : undefined} />
            </Overlay>
          </Modal>
          <ScrollView
            style={{ flex: 1, backgroundColor: surveyContentBackgroundColor }}
            contentContainerStyle={{ flexGrow: 1, backgroundColor: surveyContentBackgroundColor, paddingBottom: mobileActionBarHeight }}
          >
            <>
              {surveyLoadBlockedMessageResolved ? (
                getSurveyBlockedComponent()
              ) : (
                <>
                  <View
                    style={{
                      width: '100%' as any,
                      paddingVertical: 16,
                      borderBottomWidth: 1,
                      borderBottomColor: theme.borderColorLight as any,
                      backgroundColor: theme.background.primary as any,
                      justifyContent: 'center',
                      flexDirection: 'row',
                    }}
                  >
                    <View
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: isFirstSurveyStep && !hasPreviousSurvey() ? 'center' : 'space-between',
                        width: dimension.width >= 720 ? SURVEY_CONTENT_WIDTH : '100%',
                        paddingHorizontal: dimension.width >= 720 ? 0 : dimension.width >= 600 ? 40 : 24,
                      }}
                    >
                      <View
                        style={{
                          display: 'flex' as any,
                          flexDirection: 'row' as any,
                          alignItems: 'center' as any,
                          justifyContent: 'center' as any,
                          height: 64,
                          width: 64,
                          marginRight: 16,
                          borderRadius: 32,
                          borderWidth: 1,
                          borderColor: theme.borderColorLight,
                          overflow: 'hidden' as any,
                          backgroundColor: theme.background.secondary,
                        }}
                      >
                        {logo}
                      </View>
                      <View style={{ flexGrow: 1, flexShrink: 1 }}>
                        <Label numberOfLines={3} variant={LabelType.titleLargeNoCaps}>
                          {missionSurvey?.subscriptionInfo?.name}
                        </Label>
                        <Label numberOfLines={3} variant={LabelType.titleSmall}>
                          {missionSurvey?.branchInfo?.address}
                        </Label>
                      </View>
                      {dimension.width <= 1200 && (
                        <View style={{ height: '100%' }}>
                          <Pressable onPress={goToMyMissionsList}>
                            <Image style={{ width: 16, height: 16 }} source={require('../../../../assets/close-button.png')} />
                          </Pressable>
                        </View>
                      )}
                    </View>
                  </View>
                  {!surveyError && !surveyCompleted && survey ? (
                    <View style={{ alignItems: 'center', position: 'relative' }}>
                      {showSideNavigation && (
                        <View style={{ marginTop: 48, width: 80, position: 'absolute', zIndex: 1000, left: sideNavigationOffset }}>
                          {(!isFirstSurveyStep || hasPreviousSurvey()) && (
                            <Pressable
                              style={{ alignItems: 'center' }}
                              onPress={goToPreviousSurveyPage}
                            >
                              <Image
                                style={{
                                  height: 60,
                                  width: 60,
                                }}
                                source={require('../../../../assets/previous-button.png')}
                              />
                              <Label style={{ color: theme.text.darkTurquoiseTwo, textTransform: 'uppercase', marginTop: 4 }}>{t('previous')}</Label>
                            </Pressable>
                          )}
                        </View>
                      )}
                      {showSideNavigation && (
                        <Pressable
                          style={{
                            marginTop: 48,
                            alignItems: 'center',
                            width: 80,
                            position: 'absolute',
                            zIndex: 1000,
                            right: sideNavigationOffset,
                          }}
                          onPress={goToNextSurveyPage}
                        >
                          <Image
                            style={{
                              height: 60,
                              width: 60,
                            }}
                            source={require('../../../../assets/next-button.png')}
                          />
                          <Label style={{ color: theme.text.darkTurquoiseTwo, textTransform: 'uppercase', marginTop: 4 }}>
                            {t(isLastSurveyStep && !currentMission.nextForm ? 'complete' : 'next')}
                          </Label>
                        </Pressable>
                      )}
                      <SurveyRenderer model={survey} />
                    </View>
                  ) : (
                    <></>
                  )}

                  {!surveyError && surveyCompleted && getEmptyComponent()}
                  {surveyError && (
                    <SurveyError
                      error={surveyError}
                      onMyMissionsClick={() => navigation.navigate('mymissions' as never)}
                      onTryAgainClick={() => {
                        const activeMission = currentMissionRef.current;
                        const activeAnswerIndex = surveyAnswersRef.current?.findIndex((answer) => answer.formVersionId === activeMission?.formVersionId) ?? -1;

                        if (activeMission && activeAnswerIndex >= 0) {
                          apiMissionsMutation.mutate({
                            completionPrompt: getCompletionPrompt(),
                            snapshot: {
                              formVersionId: activeMission.formVersionId,
                              currentMissionFormVersionId: activeMission.formVersionId,
                              data: cloneSurveyData(draftSurveyJsAnswersRef.current?.[activeAnswerIndex] ?? survey?.data ?? {}),
                              pageNo: survey?.currentPageNo ?? currentPageNoRef.current,
                              currentElementName: survey?.currentElementName || currentElementNameRef.current,
                            },
                          });
                        }
                      }}
                      onContactClick={() => {
                        Linking.openURL(
                          buildSurveyErrorSupportMailto({
                            to: 'info@lanla.com',
                            subject: tMission('mission-failed-title'),
                            language: userPrefs.uiLanguage,
                            missionAssignationId: id,
                            errorStatus: getSurveyErrorStatus(surveyError),
                            errorResponse: { code: getSurveySubmissionErrorKey(surveyError) },
                          }),
                        );
                      }}
                    />
                  )}
                </>
              )}
            </>
          </ScrollView>
          {showMobileActionBar && (
            <View
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: -bottomInset,
              }}
            >
              <LinearGradient
                colors={['#024252', '#035164', '#43A49E']}
                start={{ x: 0, y: -0.5 }}
                end={{ x: 1, y: 1 }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: isFirstSurveyStep ? 'flex-end' : 'space-between',
                  width: '100%',
                  paddingHorizontal: showSideNavigation ? 0 : dimension.width >= 600 ? 40 : 24,
                  paddingTop: 10,
                  paddingBottom: 10 + bottomInset,
                }}
              >
                <View style={{ flexDirection: 'row', gap: 40 }}>
                  <View style={{ width: 60 }}>
                    {!isFirstSurveyStep && (
                      <Pressable
                        onPress={goToPreviousSurveyPage}
                      >
                        <Image
                          style={{
                            height: 60,
                            width: 60,
                          }}
                          source={require('../../../../assets/previous-button.png')}
                        />
                      </Pressable>
                    )}
                  </View>
                  <Pressable
                    onPress={goToNextSurveyPage}
                  >
                    <Image
                      style={{
                        height: 60,
                        width: 60,
                      }}
                      source={require('../../../../assets/next-button.png')}
                    />
                  </Pressable>
                </View>
              </LinearGradient>
            </View>
          )}
        </View>
      )}
    </>
  );
};

const PercentageProgressBar = (props: any) => {
  const theme = useCustomTheme();

  return (
    <View style={{ width: '100%', position: 'absolute', left: 0, top: 0, height: 3, backgroundColor: theme.borderColorLight }}>
      <View
        style={{
          width: `${(getSurveyProgress(props.model.survey) / 100) * props.model.chunk + props.model.floorPercentage()}%` as any,
          backgroundColor: theme.primary ?? ('black' as any),
          position: 'absolute',
          top: 0,
          left: 0,
          zIndex: 100,
          height: '100%',
        }}
      ></View>
    </View>
  );
};

export default SurveyJs;
